import { fetchApi } from './api';
import { setDisplayNames } from '../components/credit-limits/config/displayNames';
import type {
    ApproverUpdateBody,
    CreateRequestBody,
    CreditLimitComment,
    CreditLimitConfig,
    CreditLimitConfigWriteResult,
    FinanceUpdateBody,
    SettingsUpdateBody,
    TiersUpdateBody,
    CreditLimitRequestDto,
    RequestActionBody,
    RequestListDto,
    OverviewResponse,
    OverviewScope,
    OpenInvoicesResponse,
    SalesTransaction,
    OpenOrder,
    OpenOrderTypeFilter,
} from '../types/creditLimits';

async function putConfig(path: string, body: unknown, method: 'PUT' | 'POST' = 'PUT'): Promise<CreditLimitConfigWriteResult> {
    const result = await fetchApi<CreditLimitConfigWriteResult>(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    // After the commit the API may fail to read the configuration back and answer just `{ changed }` (B6a). The save
    // went through, so read the configuration again instead of handing the tab an answer without rules.
    if (!Array.isArray(result?.tiers)) {
        const changed = result?.changed ?? 0;
        try {
            return { ...(await creditLimitsApi.getConfig()), changed };
        } catch {
            return { changed } as CreditLimitConfigWriteResult;
        }
    }
    setDisplayNames(result?.names);
    return result;
}

function navPost(id: number, step: string, body: unknown): Promise<CreditLimitRequestDto> {
    return fetchApi(`/credit-limits/requests/${encodeURIComponent(String(id))}/${step}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

export const creditLimitsApi = {
    // Overview grid: every customer in one answer, sorted and filtered in the browser (masterplan §2, A2)
    async getOverview(scope: OverviewScope): Promise<OverviewResponse> {
        return fetchApi(`/credit-limits/overview?scope=${scope}`);
    },

    // Approval rules and the roles of the signed-in person (finance, smFor, btmFor). The display names it carries go
    // to the shared name store (B5), so every dialog and grid shows the same real names.
    async getConfig(): Promise<CreditLimitConfig> {
        const config = await fetchApi<CreditLimitConfig>('/credit-limits/config');
        setDisplayNames(config?.names);
        return config;
    },

    // Approval rules, Finance only (B5). Each answers with the whole configuration again plus `changed`.
    // Errors keep the HTTP status: 400 (a value is out of range), 403 (not Finance), 409 (last Finance person, duplicate).
    async putTiers(body: TiersUpdateBody): Promise<CreditLimitConfigWriteResult> {
        return putConfig('/credit-limits/config/tiers', body);
    },

    async putApprover(body: ApproverUpdateBody): Promise<CreditLimitConfigWriteResult> {
        return putConfig('/credit-limits/config/approvers', body);
    },

    async putSettings(body: SettingsUpdateBody): Promise<CreditLimitConfigWriteResult> {
        return putConfig('/credit-limits/config/settings', body);
    },

    async putFinance(body: FinanceUpdateBody): Promise<CreditLimitConfigWriteResult> {
        return putConfig('/credit-limits/config/finance', body);
    },

    // Limit requests (B3/B4; contract api/src/functions/credit-limits/shared/request-dto.ts). Errors keep the HTTP
    // status on the thrown ApiError: 400 (same limit, missing reason), 403 (no role), 409 (open request, conflict).
    async getRequests(): Promise<RequestListDto> {
        return fetchApi('/credit-limits/requests');
    },

    async createRequest(body: CreateRequestBody): Promise<CreditLimitRequestDto> {
        return fetchApi('/credit-limits/requests', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    },

    async requestAction(id: number, body: RequestActionBody): Promise<CreditLimitRequestDto> {
        return fetchApi(`/credit-limits/requests/${encodeURIComponent(String(id))}/actions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    },

    // NAV (C2; contract C3, Finance only, the answer is the request). Errors keep the status: 403 (not Finance),
    // 409 (wrong state, NAV refused), 423 (NAV writing is locked, see `navUnlock`).
    async navWrite(id: number): Promise<CreditLimitRequestDto> {
        return navPost(id, 'nav-write', {});
    },

    async navManual(id: number, comment: string | null): Promise<CreditLimitRequestDto> {
        return navPost(id, 'nav-manual', { comment });
    },

    async navCheck(id: number): Promise<CreditLimitRequestDto> {
        return navPost(id, 'nav-check', {});
    },

    // Lifts the NAV write lock (Finance only); answers the whole configuration.
    async navUnlock(confirmPasswordUpdated = false): Promise<CreditLimitConfigWriteResult> {
        return putConfig('/credit-limits/config/nav-unlock', confirmPasswordUpdated ? { confirmPasswordUpdated: true } : {}, 'POST');
    },

    // Open items of one customer at document level
    async getOpenInvoices(customerCode: string): Promise<OpenInvoicesResponse> {
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/open-invoices`);
    },

    // Comments
    async getComments(customerCode: string): Promise<CreditLimitComment[]> {
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/comments`);
    },

    async createComment(customerCode: string, commentText: string, commentType: string = 'note'): Promise<CreditLimitComment> {
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/comments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ commentText, commentType }),
        });
    },

    async updateComment(customerCode: string, commentId: number, commentText: string): Promise<CreditLimitComment> {
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/comments/${commentId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ commentText }),
        });
    },

    async deleteComment(customerCode: string, commentId: number): Promise<void> {
        await fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },

    // Drill-down
    async getSalesTransactions(customerCode: string): Promise<{
        transactions: SalesTransaction[];
        totals: { totalNetSalesLCY: number; transactionCount: number };
    }> {
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/sales-transactions`);
    },

    async getOpenOrders(customerCode: string, type?: OpenOrderTypeFilter): Promise<{
        orders: OpenOrder[];
        totals: { totalOutstandingEUR: number; orderCount: number };
    }> {
        const qs = type ? `?type=${type}` : '';
        return fetchApi(`/credit-limits/${encodeURIComponent(customerCode)}/open-orders${qs}`);
    },

    // Email
    async suggestRecipients(customerCode?: string): Promise<{
        suggestedSalesManager: { email: string; name: string } | null;
        allActiveEmployees: { email: string; name: string }[];
    }> {
        const qs = customerCode ? `?customerCode=${encodeURIComponent(customerCode)}` : '';
        return fetchApi(`/credit-limits/emails/suggest-recipients${qs}`);
    },

    async sendEmail(payload: {
        to: { email: string; name?: string }[];
        cc?: { email: string; name?: string }[];
        subject: string;
        htmlBody: string;
        plainBodyPreview?: string;
        contextType?: string;
        contextKey?: string | null;
        contextPayload?: unknown;
    }): Promise<{ emailLogId: number; sentBy: string; recipientCount: number }> {
        return fetchApi('/credit-limits/emails/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    },
};
